import { useMemo } from 'react';
import {
  Badge,
  Callout,
  Card,
  DataGrid,
  Money,
  PageHeader,
  SkeletonRows,
  TotalRow,
  addDecimal,
  formatMoney,
  isZeroDecimal,
  type ColumnDef,
} from '@biovet/ui';
import { useResource, type AgeingRow, type ApAgeingRow, type CashPositionRow } from '../lib/api.js';


export function CashPositionScreen() {
  const { data, error, loading } = useResource<{ rows: CashPositionRow[] }>('/cash-position');
  const rows = data?.rows ?? [];

  const total = useMemo(() => rows.reduce((acc, r) => addDecimal(acc, r.balanceBase), '0'), [rows]);

  const columns = useMemo<ColumnDef<CashPositionRow, unknown>[]>(
    () => [
      {
        id: 'code',
        header: 'Code',
        accessorKey: 'code',
        size: 90,
        cell: ({ getValue }) => (
          <span className="bb-mono text-[12.5px] text-[var(--ink-2)]">{String(getValue())}</span>
        ),
      },
      {
        id: 'name',
        header: 'Account',
        accessorKey: 'name',
        cell: ({ getValue }) => <span className="text-[13px]">{String(getValue())}</span>,
      },
      {
        id: 'kind',
        header: 'Type',
        accessorKey: 'kind',
        size: 100,
        cell: ({ getValue }) => (
          <span className="text-[12.5px] capitalize text-[var(--ink-2)]">
            {String(getValue()).toLowerCase()}
          </span>
        ),
      },
      {
        id: 'balance',
        header: 'Balance PKR',
        accessorKey: 'balanceBase',
        size: 160,
        meta: { align: 'right' },
        cell: ({ getValue }) => {
          const v = String(getValue());
          return isZeroDecimal(v) ? (
            <span className="bb-num text-[12.5px] text-[var(--ink-3)]">—</span>
          ) : (
            <Money value={v} className="text-[12.5px]" />
          );
        },
      },
      {
        id: 'lastMovement',
        header: 'Last movement',
        accessorKey: 'lastMovement',
        size: 130,
        meta: { align: 'right' },
        cell: ({ getValue }) => (
          <span className="bb-num text-[12.5px] text-[var(--ink-3)]">
            {(getValue() as string | null) ?? '—'}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        breadcrumb="Dashboard / Cash & Bank Position"
        title="Cash and bank position"
        description="Live balances from posted vouchers, grouped by cash and bank accounts."
      />
      {error && (
        <Callout tone="danger" title="Could not load cash position">
          {error}
        </Callout>
      )}
      <Card flush>
        {loading ? (
          <SkeletonRows rows={6} cols={5} />
        ) : rows.length === 0 ? (
          <div className="bb-pad py-8 text-center text-[12.5px] text-[var(--ink-3)]">
            No cash or bank accounts have posted movements yet.
          </div>
        ) : (
          <DataGrid
            columns={columns}
            data={rows}
            footer={
              <TotalRow
                cells={[
                  { content: '' },
                  { content: 'Total' },
                  { content: '' },
                  { content: formatMoney(total, { decimals: 0 }), align: 'right', numeric: true },
                  { content: '' },
                ]}
              />
            }
          />
        )}
      </Card>
    </>
  );
}


export function ArAgeingScreen() {
  const { data, error, loading } = useResource<{ rows: AgeingRow[] }>('/ar-ageing');
  const rows = data?.rows ?? [];

  const totals = useMemo(
    () => ({
      current: rows.reduce((a, r) => addDecimal(a, r.current), '0'),
      d30: rows.reduce((a, r) => addDecimal(a, r.d30), '0'),
      d60: rows.reduce((a, r) => addDecimal(a, r.d60), '0'),
      d90: rows.reduce((a, r) => addDecimal(a, r.d90), '0'),
      d90plus: rows.reduce((a, r) => addDecimal(a, r.d90plus), '0'),
      total: rows.reduce((a, r) => addDecimal(a, r.total), '0'),
    }),
    [rows],
  );

  const columns = useMemo<ColumnDef<AgeingRow, unknown>[]>(
    () => [
      {
        id: 'party',
        header: 'Customer',
        accessorKey: 'partyName',
        cell: ({ getValue }) => <span className="text-[13px]">{String(getValue())}</span>,
      },
      ...bucketColumns<AgeingRow>(),
    ],
    [],
  );

  return (
    <>
      <PageHeader
        breadcrumb="Dashboard / Receivables Ageing"
        title="Receivable ageing by customer"
        description="Who owes you money, and how overdue each customer is."
      />
      {error && (
        <Callout tone="danger" title="Could not load receivables ageing">
          {error}
        </Callout>
      )}
      <AgeingGrid
        loading={loading}
        rows={rows}
        columns={columns}
        totals={totals}
        empty="No outstanding receivables."
      />
    </>
  );
}


export function ApAgeingScreen() {
  const { data, error, loading } = useResource<{ rows: ApAgeingRow[] }>('/ap-ageing');
  const rows = data?.rows ?? [];

  const totals = useMemo(
    () => ({
      current: rows.reduce((a, r) => addDecimal(a, r.current), '0'),
      d30: rows.reduce((a, r) => addDecimal(a, r.d30), '0'),
      d60: rows.reduce((a, r) => addDecimal(a, r.d60), '0'),
      d90: rows.reduce((a, r) => addDecimal(a, r.d90), '0'),
      d90plus: rows.reduce((a, r) => addDecimal(a, r.d90plus), '0'),
      total: rows.reduce((a, r) => addDecimal(a, r.total), '0'),
    }),
    [rows],
  );

  const columns = useMemo<ColumnDef<ApAgeingRow, unknown>[]>(
    () => [
      {
        id: 'party',
        header: 'Supplier',
        accessorKey: 'partyName',
        cell: ({ getValue }) => <span className="text-[13px]">{String(getValue())}</span>,
      },
      {
        id: 'origin',
        header: 'Type',
        accessorKey: 'origin',
        size: 120,
        cell: ({ row }) => (
          <Badge tone={row.original.origin === 'FOREIGN' ? 'warn' : 'mute'}>
            {row.original.origin === 'FOREIGN' ? 'Foreign · USD' : 'Local'}
          </Badge>
        ),
      },
      ...bucketColumns<ApAgeingRow>(),
    ],
    [],
  );

  return (
    <>
      <PageHeader
        breadcrumb="Dashboard / Payables Ageing"
        title="Payable ageing by supplier"
        description="Who you owe money to, and how overdue each supplier is."
      />
      <AgeingGrid
        loading={loading}
        rows={rows}
        columns={columns}
        totals={totals}
        empty="No outstanding payables."
      />
    </>
  );
}


function bucketColumns<T extends AgeingRow>(): ColumnDef<T, unknown>[] {
  const col = (id: string, header: string, key: keyof AgeingRow): ColumnDef<T, unknown> => ({
    id,
    header,
    accessorKey: key as string,
    size: 130,
    meta: { align: 'right' },
    cell: ({ getValue }) => {
      const v = String(getValue());
      return isZeroDecimal(v) ? (
        <span className="bb-num text-[12.5px] text-[var(--ink-3)]">—</span>
      ) : (
        <span className="bb-num text-[12.5px] tabular-nums">{formatMoney(v, { decimals: 0 })}</span>
      );
    },
  });

  return [
    col('current', 'Current', 'current'),
    col('d30', '1–30', 'd30'),
    col('d60', '31–60', 'd60'),
    col('d90', '61–90', 'd90'),
    col('d90plus', '90+', 'd90plus'),
    {
      id: 'total',
      header: 'Total',
      accessorKey: 'total',
      size: 140,
      meta: { align: 'right' },
      cell: ({ getValue }) => (
        <span className="bb-num text-[12.5px] font-semibold tabular-nums">
          {formatMoney(String(getValue()), { decimals: 0 })}
        </span>
      ),
    },
  ];
}

function AgeingGrid<T extends AgeingRow>({
  loading,
  rows,
  columns,
  totals,
  empty,
}: {
  loading: boolean;
  rows: T[];
  columns: ColumnDef<T, unknown>[];
  totals: Record<string, string>;
  empty: string;
}) {
  return (
    <Card flush>
      {loading ? (
        <SkeletonRows rows={6} cols={7} />
      ) : rows.length === 0 ? (
        <div className="bb-pad py-8 text-center text-[12.5px] text-[var(--ink-3)]">{empty}</div>
      ) : (
        <DataGrid
          columns={columns}
          data={rows}
          footer={
            <TotalRow
              cells={[
                { content: 'Total' },
                ...(columns.length > 7 ? [{ content: '' }] : []),
                {
                  content: formatMoney(totals.current ?? '0', { decimals: 0 }),
                  align: 'right' as const,
                  numeric: true,
                },
                {
                  content: formatMoney(totals.d30 ?? '0', { decimals: 0 }),
                  align: 'right' as const,
                  numeric: true,
                },
                {
                  content: formatMoney(totals.d60 ?? '0', { decimals: 0 }),
                  align: 'right' as const,
                  numeric: true,
                },
                {
                  content: formatMoney(totals.d90 ?? '0', { decimals: 0 }),
                  align: 'right' as const,
                  numeric: true,
                },
                {
                  content: formatMoney(totals.d90plus ?? '0', { decimals: 0 }),
                  align: 'right' as const,
                  numeric: true,
                },
                {
                  content: formatMoney(totals.total ?? '0', { decimals: 0 }),
                  align: 'right' as const,
                  numeric: true,
                },
              ]}
            />
          }
        />
      )}
    </Card>
  );
}
